import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronDown, ChevronLeft, Download, ExternalLink, Loader2, Save, Trash2 } from "lucide-react";
import { RelatedScripturesEditor, toGroups, toRows } from "./RelatedScripturesEditor";
import LookCloserEditor from "./LookCloserEditor";

const WHO_GROUPS = [["god", "God & Jesus"], ["prophets", "Prophets"], ["kings", "Kings, Rulers & Psalmists"], ["disciples", "Disciples"], ["people", "Everyday People"]];
const THEMES = ["God's Character", "The Identity of Jesus", "Suffering & Lament", "Faith & Fear", "Sin & Repentance", "Salvation & Grace", "Wisdom & Justice", "Love & Community", "Identity & Purpose"];
const LEGACY_THEMES = ["Identity & Calling", "Faith & Obedience", "Suffering & Doubt", "Morality & Law", "Purpose & Meaning"];
const QUESTION_TYPES = ["Rhetorical", "Genuine Inquiry", "Challenge", "Corrective", "Lament", "Rebuking", "Probing", "Accusatory", "Deliberative", "Wonder/Awe", "Self-Reflective", "Cross-Examination", "Complaint", "Instructional", "Divine Initiative", "Intercessory", "Invitational", "Prophetic", "Wisdom/Philosophical", "Request/Petition"];
const STUDY = [["purpose", "Why It Was Asked"], ["response", "The Response"], ["historical_context", "Historical Context"], ["commentary", "The Bigger Picture"], ["application", "For Your Life Today"]];

const EMPTY = {
  reference: "", question_kjv: "", question_esv: "", verse_kjv: "", verse_esv: "", who_asked: "", who_asked_detail: "", who_group: "people", asked_to: "",
  asked_to_detail: "", scene: "", type: "", theme: THEMES[0], themes: "", question_types: "", section: "", pericope: "", response_type: "", reviewer_notes: "", purpose: "", response: "",
  commentary: "", application: "", historical_context: "", reflection_prompt: "", ask_god: "", ask_each_other: "", related_ids: "", share_hook: "", related_rows: [], source_url: "", status: "published",
};
const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

function TagField({ label, hint, value, onChange, options, testId }) {
  const current = list(value);
  const toggle = (t) => onChange({ target: { value: (current.includes(t) ? current.filter((x) => x !== t) : [...current, t]).join(", ") } });
  return (
    <Field label={label} hint={hint}>
      <Input value={value} onChange={onChange} data-testid={testId} />
      <div className="mt-2 flex flex-wrap gap-1">
        {options.map((t) => (
          <button key={t} type="button" onClick={() => toggle(t)} data-testid={`${testId}-opt-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full border px-2 py-0.5 font-mono text-[10px] transition-colors ${current.includes(t) ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-gold hover:text-foreground"}`}>{t}</button>
        ))}
      </div>
    </Field>
  );
}

function toForm(q) {
  return {
    ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, q[k] ?? EMPTY[k]])),
    who_asked: (q.who_asked || []).join(", "), asked_to: (q.asked_to || []).join(", "), question_types: (q.question_types || []).join(", "), themes: (q.themes || (q.theme ? [q.theme] : [])).join(", "),
    related_ids: (q.related_ids || []).join(", "),
    related_rows: toRows(q.related_scriptures), section: q.section || "", source_url: q.source_url || "",
    status: q.status || "published",
  };
}

function Field({ label, hint, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      {hint && <span className="ml-2 text-[11px] text-muted-foreground/70">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

const SelectBox = ({ value, onChange, options, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

import { QuestionActivity } from "./QuestionActivity";

export default function QuestionEditor({ id, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [refresh, setRefresh] = useState(!id);
  const [busy, setBusy] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const { data: existing, isLoading } = useQuery({
    queryKey: ["admin-question", id],
    queryFn: async () => (await api.get(`/admin/questions/${id}`)).data,
    enabled: !!id,
  });
  useEffect(() => { if (existing) setForm(toForm(existing)); }, [existing]);

  const lookup = async () => {
    if (!form.reference.trim()) return toast.error("Enter a reference first, e.g. John 3:4");
    setBusy(true);
    try {
      const r = (await api.get("/admin/lookup", { params: { reference: form.reference.trim() } })).data;
      setForm((f) => ({ ...f, reference: r.reference, verse_kjv: r.verse_kjv, verse_esv: r.verse_esv }));
      toast.success(`Fetched ${r.reference} — ${r.defined_words.length} Strong's words ready.`);
      setRefresh(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const { related_rows, ...rest } = form;
    const body = {
      ...rest, related_scriptures: toGroups(related_rows), who_asked: list(form.who_asked), asked_to: list(form.asked_to), question_types: list(form.question_types), themes: list(form.themes), related_ids: list(form.related_ids),
      section: form.section || null, source_url: form.source_url || null, refresh_strongs: refresh,
    };
    setBusy(true);
    try {
      const r = id ? await api.put(`/admin/questions/${id}`, body) : await api.post("/admin/questions", body);
      if (r.data.strongs_error) toast.warning(`Saved, but Strong's lookup failed: ${r.data.strongs_error}`);
      else toast.success(id ? "Question updated." : "Question created.");
      onSaved(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${existing?.question_kjv}" (${existing?.reference})? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success("Question deleted.");
      onSaved(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (id && isLoading) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;

  return (
    <div className="space-y-6" data-testid="question-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onClose} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="editor-back">
          <ChevronLeft className="h-4 w-4" /> All questions
        </button>
        <div className="flex items-center gap-3">
          {id && (
            <a href={`/question/${id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-gold hover:text-foreground" data-testid="editor-view-page">
              <ExternalLink className="h-3.5 w-3.5" /> View page
            </a>
          )}
          <h2 className="font-serif text-2xl font-semibold text-foreground">{id ? `Edit ${existing?.reference}` : "New question"}</h2>
        </div>
      </div>

      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field label="Reference" hint="e.g. John 3:4 — the id and Bible-order position come from this">
              <Input value={form.reference} onChange={set("reference")} placeholder="Book chapter:verse" data-testid="editor-reference" />
            </Field>
          </div>
          <Button type="button" variant="outline" onClick={lookup} disabled={busy} className="rounded-full" data-testid="editor-lookup">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Fetch verse + Strong&apos;s
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Question (KJV)"><Textarea rows={2} value={form.question_kjv} onChange={set("question_kjv")} data-testid="editor-question-kjv" /></Field>
          <Field label="Question (ESV)"><Textarea rows={2} value={form.question_esv} onChange={set("question_esv")} data-testid="editor-question-esv" /></Field>
          <Field label="Full verse (KJV)"><Textarea rows={3} value={form.verse_kjv} onChange={set("verse_kjv")} data-testid="editor-verse-kjv" /></Field>
          <Field label="Full verse (ESV)"><Textarea rows={3} value={form.verse_esv} onChange={set("verse_esv")} data-testid="editor-verse-esv" /></Field>
        </div>
      </section>

      <section className="grid gap-4 rounded-2xl border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Asked by" hint="comma-separated"><Input value={form.who_asked} onChange={set("who_asked")} data-testid="editor-who-asked" /></Field>
        <Field label="Asked by details" hint="shown under the name, e.g. “the LORD God, walking in the garden”"><Input value={form.who_asked_detail || ""} onChange={set("who_asked_detail")} data-testid="editor-who-asked-detail" /></Field>
        <Field label="Voice group"><SelectBox value={form.who_group} onChange={set("who_group")} options={WHO_GROUPS} testId="editor-who-group" /></Field>
        <Field label="Asked to" hint="comma-separated"><Input value={form.asked_to} onChange={set("asked_to")} data-testid="editor-asked-to" /></Field>
        <Field label="Asked to details" hint="shown under the name, e.g. “Adam, hiding among the trees”"><Input value={form.asked_to_detail || ""} onChange={set("asked_to_detail")} data-testid="editor-asked-to-detail" /></Field>
        <div className="hidden lg:block" aria-hidden="true" />
        <TagField label="Question type" value={form.question_types} onChange={set("question_types")} options={QUESTION_TYPES} testId="editor-question-types" />
        <TagField label="Themes" hint="first one is primary" value={form.themes} onChange={set("themes")} options={[...THEMES, ...LEGACY_THEMES.filter((t) => list(form.themes).includes(t))]} testId="editor-themes" />
        <Field label="Response type" hint="admin-only"><Input value={form.response_type || ""} onChange={set("response_type")} placeholder="Direct answer, Self-answered, Action or sign, No recorded response…" data-testid="editor-response-type" /></Field>
        <Field label="Section" hint="e.g. Primeval History"><Input value={form.section} onChange={set("section")} data-testid="editor-section" /></Field>
        <Field label="Pericope" hint="e.g. Ruth Clave unto Naomi"><Input value={form.pericope || ""} onChange={set("pericope")} data-testid="editor-pericope" /></Field>
        <div className="sm:col-span-2 lg:col-span-3"><Field label="The Scene" hint="who, where, what's happening — one or two sentences for a first-time visitor"><Textarea rows={2} value={form.scene || ""} onChange={set("scene")} data-testid="editor-scene" /></Field></div>
      </section>

      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        {STUDY.map(([k, l]) => (
          <Field key={k} label={l} hint="scripture references are auto-linked"><Textarea rows={5} value={form[k]} onChange={set(k)} data-testid={`editor-${k}`} /></Field>
        ))}
        <Field label="Reflection prompt"><Input value={form.reflection_prompt} onChange={set("reflection_prompt")} data-testid="editor-reflection-prompt" /></Field>
        <div className="space-y-4 rounded-xl border border-dashed border-border p-4" data-testid="editor-your-turn">
          <p className="font-mono text-[10px] uppercase tracking-wider text-gold">Your Turn to Ask</p>
          <Field label="Ask God"><Input value={form.ask_god || ""} onChange={set("ask_god")} data-testid="editor-ask-god" /></Field>
          <Field label="Ask Yourself and Others" hint="one question per line"><Textarea rows={3} value={form.ask_each_other || ""} onChange={set("ask_each_other")} data-testid="editor-ask-each-other" /></Field>
        </div>
        <Field label="Keep questioning" hint="question ids, comma-separated, e.g. mark-14-60, acts-7-1 — leave blank for automatic picks"><Input value={form.related_ids} onChange={set("related_ids")} data-testid="editor-related-ids" /></Field>
        <Field label="Reviewer notes" hint="admin-only · never shown on the site"><Textarea rows={3} value={form.reviewer_notes || ""} onChange={set("reviewer_notes")} className="text-xs" data-testid="editor-reviewer-notes" /></Field>
        <Field label="Share card hook" hint='short curiosity line on the share image, e.g. "See how Jesus answered" — blank uses an automatic one'><Input value={form.share_hook || ""} onChange={set("share_hook")} data-testid="editor-share-hook" /></Field>
      </section>

      {id && <LookCloserEditor questionId={id} />}

      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <button type="button" onClick={() => setRelatedOpen((o) => !o)} aria-expanded={relatedOpen} className="flex w-full items-start gap-2 text-left" data-testid="editor-related-toggle">
          <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${relatedOpen ? "rotate-180" : ""}`} />
          <div>
            <p className="flex items-center gap-2 font-serif text-xl font-semibold text-foreground">
              Related scriptures
              <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-muted-foreground" data-testid="editor-related-count">{form.related_rows.length} ref{form.related_rows.length === 1 ? "" : "s"}</span>
            </p>
            <p className="text-xs text-muted-foreground">shown grouped by category on the question page, in this order</p>
          </div>
        </button>
        {relatedOpen && <RelatedScripturesEditor rows={form.related_rows} onChange={set("related_rows")} testId="editor-related" />}
        <Field label="Source URL"><Input value={form.source_url} onChange={set("source_url")} data-testid="editor-source-url" /></Field>
      </section>

      {id && <QuestionActivity id={id} />}

      <div className="sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={form.status === "published"} onCheckedChange={(v) => setForm((f) => ({ ...f, status: v ? "published" : "draft" }))} data-testid="editor-status" />
            {form.status === "published" ? "Published — visible on the site" : "Draft — hidden until published"}
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={refresh} onCheckedChange={setRefresh} data-testid="editor-refresh-strongs" />
            Rebuild Strong&apos;s on save
          </label>
        </div>
        <div className="flex gap-2">
          {id && (
            <Button type="button" variant="ghost" onClick={remove} disabled={busy} className="rounded-full text-destructive hover:text-destructive" data-testid="editor-delete">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="editor-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="editor-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} {id ? "Save changes" : "Create question"}
          </Button>
        </div>
      </div>
    </div>
  );
}
